import { 
  Clock, 
  CheckCircle, 
  AlertTriangle, 
  BarChart3, 
  Target, 
  Wrench,
  Users
} from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { Reports4KPIConfig } from "@/pages/Reports";

interface Reports4KPIConfigPopupProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  config: Reports4KPIConfig;
  onConfigChange: (config: Reports4KPIConfig) => void;
}

const kpiCategories = [
  {
    title: "Diário de Obra",
    items: [
      {
        key: "pontualidadeRDO" as keyof Reports4KPIConfig,
        title: "Pontualidade no Preenchimento dos RDOs",
        description: "Percentual de disciplina no registro diário",
        icon: Clock,
      },
    ],
  },
  {
    title: "Gestor de Tarefas com IA",
    items: [
      {
        key: "cumprimentoPrazos" as keyof Reports4KPIConfig,
        title: "Taxa de Cumprimento de Tarefas no Prazo",
        description: "Quantas tarefas são entregues dentro do prazo previsto",
        icon: CheckCircle,
      },
      {
        key: "tarefasAtrasadas" as keyof Reports4KPIConfig,
        title: "Tarefas em Atraso",
        description: "Número absoluto com indicação de tendência",
        icon: AlertTriangle,
      },
      {
        key: "tarefasStatus" as keyof Reports4KPIConfig,
        title: "Tarefas por Status",
        description: "Distribuição das tarefas em andamento",
        icon: BarChart3,
      },
    ],
  },
  {
    title: "Progresso e Recursos",
    items: [
      {
        key: "progressoFisico" as keyof Reports4KPIConfig,
        title: "Progresso Físico (Real x Planejado)",
        description: "Comparação entre curva real vs. planejada",
        icon: Target,
      },
      {
        key: "equipamentos" as keyof Reports4KPIConfig,
        title: "Equipamentos",
        description: "Distribuição entre equipamentos próprios e locados",
        icon: Wrench,
      },
      {
        key: "histogramaPessoas" as keyof Reports4KPIConfig,
        title: "Histograma de Pessoas",
        description: "Distribuição de pessoas ao longo do período",
        icon: Users,
      },
    ],
  },
];

export const Reports4KPIConfigPopup = ({ 
  open, 
  onOpenChange, 
  config, 
  onConfigChange 
}: Reports4KPIConfigPopupProps) => {
  const handleToggle = (key: keyof Reports4KPIConfig, value: boolean) => {
    onConfigChange({
      ...config,
      [key]: value,
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-xl">Configurar Indicadores (KPIs)</DialogTitle>
        </DialogHeader>
        
        <div className="space-y-6">
          {kpiCategories.map((category, categoryIndex) => (
            <div key={category.title} className="space-y-4">
              <h3 className="font-medium text-foreground">{category.title}</h3>
              
              <div className="space-y-4">
                {category.items.map((item) => {
                  const Icon = item.icon;
                  return (
                    <div key={item.key} className="flex items-start space-x-3 p-3 rounded-lg border">
                      <Icon className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                      <div className="flex-1 space-y-1">
                        <div className="flex items-center justify-between">
                          <Label 
                            htmlFor={item.key}
                            className="text-sm font-medium cursor-pointer"
                          >
                            {item.title}
                          </Label>
                          <Switch
                            id={item.key}
                            checked={config[item.key]}
                            onCheckedChange={(checked) => handleToggle(item.key, checked)}
                          />
                        </div>
                        <p className="text-xs text-muted-foreground">
                          {item.description}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
              
              {categoryIndex < kpiCategories.length - 1 && <Separator />}
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
};